import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'

// `Do` (ordinal day-of-month) lives in advancedFormat, not dayjs core.
dayjs.extend(advancedFormat)

// Every absolute date the app prints comes from here, so the house style —
// "7th June 21:05" — is defined once. Relative times (fromNow) are deliberately
// not covered: they read better as "2h ago" and have no format string to share.

const TIME = 'HH:mm'
const DAY_MONTH = 'Do MMMM'
const DAY_MONTH_YEAR = 'Do MMMM YYYY'

function isThisYear(day: dayjs.Dayjs): boolean {
  return day.isSame(dayjs(), 'year')
}

/** "7th June 21:05", or "7th June 2025, 21:05" outside the current year. */
export function formatDateTime(value: string | Date): string {
  const day = dayjs(value)
  return isThisYear(day) ? day.format(`${DAY_MONTH} ${TIME}`) : day.format(`${DAY_MONTH_YEAR}, ${TIME}`)
}

/** "15th September 2026" — for values with calendar-date semantics and no time. */
export function formatDate(value: string | Date): string {
  return dayjs(value).format(DAY_MONTH_YEAR)
}

/** "7th June", gaining the year outside the current one. For thread day dividers. */
export function formatDayLabel(value: string | Date): string {
  const day = dayjs(value)
  return day.format(isThisYear(day) ? DAY_MONTH : DAY_MONTH_YEAR)
}

/** "21:05" — only for rows already grouped under a day divider. */
export function formatTime(value: string | Date): string {
  return dayjs(value).format(TIME)
}

/**
 * "2026-06-07 21:05:33" — the action trail's event dialog, where the timestamp
 * is a value to compare and quote rather than prose, and seconds matter.
 */
export function formatTimestampPrecise(value: string | Date): string {
  return dayjs(value).format('YYYY-MM-DD HH:mm:ss')
}

/** "45m", "3h 20m", "2d 4h": how long something took, in its two largest units. */
export function formatDuration(from: string | Date, to: string | Date): string {
  const minutes = Math.max(0, Math.round(dayjs(to).diff(dayjs(from), 'minute', true)))
  if (minutes < 1) return '<1m'
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  const mins = minutes % 60
  if (days) return hours ? `${days}d ${hours}h` : `${days}d`
  if (hours) return mins ? `${hours}h ${mins}m` : `${hours}h`
  return `${mins}m`
}
